import { useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/misc/Breadcrumbs'
import { StatCard } from '@/components/misc/StatCard'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Search, Plus, MoreHorizontal, Trash2, Edit, X, Phone, MapPin, Copy, Facebook, Twitter, Instagram, Linkedin } from 'lucide-react'
import { Avatar } from '@/components/ui/Avatar'
import { getCustomers, updateCustomerStatus } from '@/services/mock/customers'
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts'

export default function CustomersPage() {
  const [customers, setCustomers] = useState([])
  const [page, setPage] = useState(1)
  const [size] = useState(10)
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [loading, setLoading] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState(null)

  const fetchCustomers = async () => {
    setLoading(true)
    try {
      const res = await getCustomers({ page, size, search, status: statusFilter })
      setCustomers(res.data)
      setTotal(res.meta.total)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCustomers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, statusFilter])

  const handleSearchChange = (e) => {
    setSearch(e.target.value)
    setPage(1)
  }

  const handleStatusChange = (status) => {
    setStatusFilter(status)
    setPage(1)
  }

  const handleUpdateStatus = async (id, status) => {
    setLoading(true)
    try {
      await updateCustomerStatus(id, status)
      await fetchCustomers()
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Customers</h1>
          <Breadcrumbs />
        </div>
      </div>

      {/* Header: stats and chart */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="space-y-4">
          <StatCard title="Total Customers" value={11040} change={14.4} changeType="percentage" />
          <StatCard title="New Customers" value={2370} change={20} changeType="percentage" />
          <StatCard title="Visitor" value={250000} change={20} changeType="percentage" format="number" />
        </div>

        <div className="lg:col-span-2 bg-card border rounded-xl p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">Customer Overview</h3>
            <div className="flex items-center gap-2">
              <button className="px-2 py-1 rounded bg-muted text-sm">This week</button>
              <button className="px-2 py-1 rounded text-sm">Last week</button>
            </div>
          </div>
          {/* Chart */}
          <div className="mt-4 h-40 flex">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={[
                  { name: 'Sun', value: 12000 },
                  { name: 'Mon', value: 30000 },
                  { name: 'Tue', value: 40000 },
                  { name: 'Wed', value: 25000 },
                  { name: 'Thu', value: 45000 },
                  { name: 'Fri', value: 38000 },
                  { name: 'Sat', value: 42000 },
                ]}
                margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="colorCust" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.12} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke="#e6edf3" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} stroke="#94a3b8" />
                <YAxis axisLine={false} tickLine={false} stroke="#94a3b8" />
                <Tooltip formatter={(value) => new Intl.NumberFormat().format(value)} />
                <Area type="monotone" dataKey="value" stroke="#10B981" fillOpacity={1} fill="url(#colorCust)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Controls and table */}
      <section className="space-y-4">
        <div className="bg-card border p-4 rounded-xl">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={() => handleStatusChange('')} className="px-3 py-1 rounded-full bg-primary text-primary-foreground text-sm">All customers ({total || 0})</button>
              <button onClick={() => handleStatusChange('ACTIVE')} className="px-3 py-1 rounded-full text-sm bg-muted/50">Active</button>
              <button onClick={() => handleStatusChange('INACTIVE')} className="px-3 py-1 rounded-full text-sm bg-muted/50">Inactive</button>
              <button onClick={() => handleStatusChange('VIP')} className="px-3 py-1 rounded-full text-sm bg-muted/50">VIP</button>
            </div>

            <div className="flex items-center gap-2 w-full md:w-auto">
              <div className="flex-1 md:flex-none">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input value={search} onChange={handleSearchChange} placeholder="Search data, users, or reports" className="pl-10" />
                </div>
              </div>

              <Button variant="primary" size="md" className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Add Customer
              </Button>
              <Button variant="ghost" size="md" className="flex items-center gap-2">
                More Action
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        <div className="lg:flex lg:items-start lg:gap-4">
          <div className="bg-card border p-4 rounded-xl flex-1">
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="p-3">Customer Id</th>
                  <th className="p-3">Name</th>
                  <th className="p-3">Phone</th>
                  <th className="p-3">Order Count</th>
                  <th className="p-3">Total Spend</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="p-4 text-center text-muted-foreground">Loading...</td>
                  </tr>
                ) : customers.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-4 text-center text-muted-foreground">No customers found</td>
                  </tr>
                ) : (
                  customers.map((c, idx) => (
                    <tr key={c.id} onClick={() => setSelectedCustomer(c)} className="border-b last:border-b-0 hover:bg-muted/50 cursor-pointer">
                      <td className="p-3 align-middle">{c.customerId}</td>
                      <td className="p-3 align-middle">{c.name}</td>
                      <td className="p-3 align-middle">{c.phone}</td>
                      <td className="p-3 align-middle">{c.orderCount}</td>
                      <td className="p-3 align-middle">{c.totalSpend.toFixed(2)}</td>
                      <td className="p-3 align-middle">
                        <span className={
                          `inline-flex items-center gap-2 text-sm font-medium ` +
                          (c.status === 'ACTIVE' ? 'text-success-600' : c.status === 'VIP' ? 'text-warning-600' : 'text-danger-600')
                        }>
                          <span className={
                            `inline-block w-2 h-2 rounded-full ` +
                            (c.status === 'ACTIVE' ? 'bg-success' : c.status === 'VIP' ? 'bg-warning' : 'bg-danger')
                          } />
                          {c.status === 'ACTIVE' ? 'Active' : c.status === 'VIP' ? 'VIP' : 'Inactive'}
                        </span>
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => alert('Edit ' + c.name)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="destructive" size="sm" onClick={() => alert('Delete ' + c.name)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleUpdateStatus(c.id, c.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')}>
                            {c.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-muted-foreground">Showing {(page - 1) * size + 1} - {Math.min(page * size, total)} of {total}</div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Previous</Button>
              <div className="px-3 py-1 rounded bg-muted text-sm">{page}</div>
              <Button variant="ghost" size="sm" disabled={page * size >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          </div>
          </div>

          {/* Right-side customer details panel (visible on large screens) */}
          <aside className="hidden lg:block w-80">
          {selectedCustomer ? (
            <div className="bg-card border rounded-xl p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <Avatar size="xl" alt={selectedCustomer.name} fallback={selectedCustomer.name} />
                  <div>
                    <div className="font-semibold">{selectedCustomer.name}</div>
                    <div className="text-sm text-muted-foreground">{(selectedCustomer.name || '').toLowerCase().replace(' ', '.') + '@example.com'}</div>
                  </div>
                </div>
                <button onClick={() => setSelectedCustomer(null)} className="text-muted-foreground hover:text-foreground">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mt-4 space-y-3">
                <div>
                  <h4 className="text-xs text-muted-foreground">Customer Info</h4>
                  <div className="mt-2 space-y-2">
                    <div className="text-sm bg-background border rounded px-3 py-2 flex items-center justify-between">
                      <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" /> {selectedCustomer.phone}</div>
                    </div>
                    <div className="text-sm bg-background border rounded px-3 py-2 flex items-center justify-between">
                      <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-muted-foreground" /> 123 Main St, NY</div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-xs text-muted-foreground">Social Media</h4>
                  <div className="mt-2 flex items-center gap-2">
                    <button className="p-2 bg-muted rounded text-muted-foreground"><Facebook className="h-4 w-4" /></button>
                    <button className="p-2 bg-muted rounded text-muted-foreground"><Twitter className="h-4 w-4" /></button>
                    <button className="p-2 bg-muted rounded text-muted-foreground"><Instagram className="h-4 w-4" /></button>
                    <button className="p-2 bg-muted rounded text-muted-foreground"><Linkedin className="h-4 w-4" /></button>
                  </div>
                </div>

                <div>
                  <h4 className="text-xs text-muted-foreground">Activity</h4>
                  <div className="mt-2 text-sm text-muted-foreground">
                    <div>Registration: 15.01.2025</div>
                    <div>Last purchase: 10.01.2025</div>
                  </div>
                </div>

                <div>
                  <h4 className="text-xs text-muted-foreground">Order overview</h4>
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    <div className="bg-background border rounded p-3 text-center">
                      <div className="text-sm text-muted-foreground">Total order</div>
                      <div className="font-semibold">{selectedCustomer.orderCount}</div>
                    </div>
                    <div className="bg-background border rounded p-3 text-center">
                      <div className="text-sm text-muted-foreground">Completed</div>
                      <div className="font-semibold">{Math.floor(selectedCustomer.orderCount * 0.8)}</div>
                    </div>
                    <div className="bg-background border rounded p-3 text-center">
                      <div className="text-sm text-muted-foreground">Canceled</div>
                      <div className="font-semibold">{Math.floor(selectedCustomer.orderCount * 0.1)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">Select a customer to see details</div>
          )}
        </aside>

        </div>

        {/* Mobile / small screen slide-over */}
        {selectedCustomer && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-black/40" onClick={() => setSelectedCustomer(null)} />
            <div className="absolute right-0 top-0 h-full w-full max-w-sm bg-card border p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <Avatar size="xl" alt={selectedCustomer.name} fallback={selectedCustomer.name} />
                  <div>
                    <div className="font-semibold">{selectedCustomer.name}</div>
                    <div className="text-sm text-muted-foreground">{(selectedCustomer.name || '').toLowerCase().replace(' ', '.') + '@example.com'}</div>
                  </div>
                </div>
                <button onClick={() => setSelectedCustomer(null)} className="text-muted-foreground hover:text-foreground">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mt-4 space-y-3">
                <div>
                  <h4 className="text-xs text-muted-foreground">Customer Info</h4>
                  <div className="mt-2 space-y-2">
                    <div className="text-sm bg-background border rounded px-3 py-2 flex items-center justify-between">
                      <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" /> {selectedCustomer.phone}</div>
                    </div>
                    <div className="text-sm bg-background border rounded px-3 py-2 flex items-center justify-between">
                      <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-muted-foreground" /> 123 Main St, NY</div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-xs text-muted-foreground">Social Media</h4>
                  <div className="mt-2 flex items-center gap-2">
                    <button className="p-2 bg-muted rounded text-muted-foreground"><Facebook className="h-4 w-4" /></button>
                    <button className="p-2 bg-muted rounded text-muted-foreground"><Twitter className="h-4 w-4" /></button>
                    <button className="p-2 bg-muted rounded text-muted-foreground"><Instagram className="h-4 w-4" /></button>
                    <button className="p-2 bg-muted rounded text-muted-foreground"><Linkedin className="h-4 w-4" /></button>
                  </div>
                </div>

                <div>
                  <h4 className="text-xs text-muted-foreground">Activity</h4>
                  <div className="mt-2 text-sm text-muted-foreground">
                    <div>Registration: 15.01.2025</div>
                    <div>Last purchase: 10.01.2025</div>
                  </div>
                </div>

                <div>
                  <h4 className="text-xs text-muted-foreground">Order overview</h4>
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    <div className="bg-background border rounded p-3 text-center">
                      <div className="text-sm text-muted-foreground">Total order</div>
                      <div className="font-semibold">{selectedCustomer.orderCount}</div>
                    </div>
                    <div className="bg-background border rounded p-3 text-center">
                      <div className="text-sm text-muted-foreground">Completed</div>
                      <div className="font-semibold">{Math.floor(selectedCustomer.orderCount * 0.8)}</div>
                    </div>
                    <div className="bg-background border rounded p-3 text-center">
                      <div className="text-sm text-muted-foreground">Canceled</div>
                      <div className="font-semibold">{Math.floor(selectedCustomer.orderCount * 0.1)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  )
}